import { useContext, useEffect } from 'react';
import QuestionLabel from './QuestionLabel';
import { ThemeContext } from '~/lib/ThemeContext';
import { ConfirmationContext } from '~/lib/ConfirmationContext';
import { useFormCustomCss } from '~/lib/FormCustomCssContext';
import { FormPart, part } from '~/lib/FormCssApi';
import type { QuestionComponentProps } from '~/lib/Questions/Questions.types';
import {
    CustomQuestionMode,
    SectionType,
    type CustomQuestionModeName,
} from '~/lib/CustomQuestions/CustomQuestionApi';
import {
    clearCustomQuestionValidity,
    setCustomQuestionValidity,
    useCustomQuestion,
} from '~/lib/CustomQuestions/CustomQuestionRegistry';
import useCustomQuestionFrame from '~/lib/CustomQuestions/useCustomQuestionFrame';
import useConfirmationActions from '~/lib/CustomQuestions/useConfirmationActions';
import useClientHistoryActions from '~/lib/CustomQuestions/useClientHistoryActions';

type CustomQuestionProps = QuestionComponentProps<any> & {
    /** The form question's own id, which the form validates against */
    questionId?: number | string | null;
    /**
     * Which situation this render is. Defaults to filling the form in - the
     *  submission panel passes 'answer', the designer passes 'preview'
     */
    mode?: CustomQuestionModeName;
    answeredAt?: string | null;
    /** The editor's unsaved draft, which is never in the registry */
    definitionOverride?: any;
};

/**
 * A question type the business wrote themselves.
 *
 * Everything the question actually renders happens inside a sandboxed iframe
 *  on an opaque origin - see ~/lib/CustomQuestions/sandboxDocument for what
 *  goes into it. This component is the part of it that belongs to the form:
 *  the label, the error, and the standard CSS hooks, so a custom question
 *  styles and validates like any other.
 */
const CustomQuestion = ({
    label,
    value,
    setValue,
    settings,
    isReadOnly,
    id,
    questionId,
    mode,
    answeredAt,
    definitionOverride,
}: CustomQuestionProps) => {
    const registered = useCustomQuestion(settings?.customQuestionId);
    const definition = definitionOverride ?? registered;
    const theme = useContext(ThemeContext);
    const customCss = useFormCustomCss();
    const confirmationContext: any = useContext(ConfirmationContext);

    // Only FormThankYou says this, so a question anywhere else - the form
    //  itself, an admin reading a submission - is never handed one
    const isConfirmationPage = !!confirmationContext?.isConfirmationPage;

    // A confirmation has no answer to fill in, which is what readonly means
    const resolvedMode: CustomQuestionModeName =
        mode ??
        (isReadOnly || isConfirmationPage
            ? CustomQuestionMode.READONLY
            : CustomQuestionMode.FILL);

    const { confirmation, onCancelEvent } = useConfirmationActions({
        isConfirmationPage,
        // Sample data in the designer and editor, but only for a question
        //  that can go on a confirmation section in the first place
        isPreview:
            resolvedMode === CustomQuestionMode.PREVIEW &&
            !!definition?.flags?.sections?.includes(SectionType.THANK_YOU),
        confirmation: confirmationContext?.customQuestionConfirmation,
        token: confirmationContext?.submission?.token,
    });

    const { onGetClientHistory, clientKey } = useClientHistoryActions({
        mode: resolvedMode,
    });

    // The form question's own id, which is what the validator looks a question
    //  up by. Deliberately not the `id` prop: the public form passes that as
    //  `answers.123`, so keying on it meant nothing ever matched and a
    //  question reporting a problem was silently let through
    const validityKey = questionId ?? id;

    const {
        frameRef,
        srcDoc,
        height,
        isReady,
        hasTimedOut,
        validity,
        frameError,
    } = useCustomQuestionFrame({
        definition,
        mode: resolvedMode,
        settings,
        value,
        setValue,
        answeredAt,
        styles: theme?.styles,
        questionId: validityKey,
        customCss,
        confirmation,
        onCancelEvent,
        onGetClientHistory,
        clientKey,
    });

    // The frame reports on its answer whenever it likes, but the form
    //  validates all at once and synchronously, so the latest word is left
    //  somewhere getValidationError can read it on submit
    useEffect(() => {
        if (resolvedMode !== CustomQuestionMode.FILL) {
            return;
        }

        setCustomQuestionValidity(validityKey, validity);

        return () => clearCustomQuestionValidity(validityKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validityKey, resolvedMode, validity.isValid, validity.message]);

    if (!definition) {
        return (
            <div>
                <QuestionLabel label={label} settings={settings} />
                <div {...part(FormPart.CONTROL)}>
                    <div className="text-gray-500 text-sm">
                        This question is still loading.
                    </div>
                </div>
            </div>
        );
    }

    const showsLabel = !definition.flags?.noLabel && !!label;

    return (
        <div>
            {showsLabel && <QuestionLabel label={label} settings={settings} />}

            <div {...part(FormPart.CONTROL)} className="relative">
                <iframe
                    ref={frameRef}
                    title={definition.name || label || 'Question'}
                    srcDoc={srcDoc}
                    /**
                     * allow-scripts and nothing else. Without
                     *  allow-same-origin the frame sits on an opaque origin,
                     *  so it cannot reach our cookies, our storage or this
                     *  page; without allow-forms, allow-popups,
                     *  allow-top-navigation and allow-modals it cannot
                     *  navigate the client away or cover the form
                     */
                    sandbox="allow-scripts"
                    referrerPolicy="no-referrer"
                    scrolling="no"
                    className={`w-full block border-0 transition-opacity ${isReady ? 'opacity-100' : 'opacity-0'}`}
                    style={{ height: `${height}px` }}
                />

                {!isReady && !hasTimedOut && (
                    <div className="absolute inset-0 flex items-center text-gray-500 text-sm">
                        Loading...
                    </div>
                )}

                {hasTimedOut && (
                    <div className="text-sm text-gray-500">
                        This question could not be loaded.
                        {!!frameError && (
                            <span className="block text-xs mt-1">
                                {frameError}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomQuestion;
